import type { ComponentMetadata } from '../types';

// Import all components from the library
import {
  // Skeleton
  Skeleton,
  SkeletonText,
  SkeletonAvatar,
  SkeletonImage,
  SkeletonCard,
  SkeletonList,
  SkeletonTable,
  SkeletonPage,
  SkeletonForm,
  // Spinner
  SpinnerCircle,
  SpinnerRing,
  SpinnerDots,
  SpinnerBars,
  SpinnerGrid,
  SpinnerWave,
  SpinnerPulse,
  // Progress
  ProgressBar,
  ProgressCircle,
  ProgressRing,
  ProgressSteps,
  // Pulse
  PulseDots,
  PulseWave,
  PulseBars,
  TypingIndicator,
  // Overlay
  LoaderOverlay,
  // Button
  ButtonSpinner,
  // Status
  SuccessCheckmark,
  ErrorIndicator,
  // Transition
  LoaderTransition,
  // Shimmer
  ShimmerBox,
  ShimmerText,
  ShimmerButton,
  // Orbit
  OrbitDots,
  OrbitRings,
  AtomLoader,
  // Bounce
  BouncingDots,
  BouncingBalls,
  // Infinity
  InfinityLoader,
  MobiusLoader,
  // Text
  LoadingText,
  // 3D
  CubeSpinner,
  FlipCard,
  PlaneRotate,
  Helix,
  PerspectiveRing,
  // Smart
  SmartSkeleton,
  DataTableSkeleton,
  FormFieldLoader,
  // Accessibility
  LiveRegion,
  // Morph
  MorphBlob,
  LiquidFill,
  WaveCircle,
  // Gradient
  GradientSpinner,
  GradientRing,
  GradientBar,
  // Particle
  ParticleBurst,
  ParticleOrbit,
  // Neon
  NeonPulse,
  NeonSpinner,
  NeonText,
  // Status (new)
  WarningIndicator,
  InfoIndicator,
  // Particle (new)
  ParticleTrail,
  ParticleField,
  // Chart (new)
  BarChartSkeleton,
  LineChartSkeleton,
  DonutChartSkeleton,
  // Gradient (new)
  GradientText,
  GradientDots,
  // Neon (new)
  NeonProgress,
  NeonDots,
} from '@lib/components';

export const COMPONENTS: ComponentMetadata[] = [
  // ==================== SKELETON COMPONENTS ====================
  {
    id: 'skeleton',
    name: 'Skeleton',
    category: 'skeleton',
    component: Skeleton,
    description: 'Base skeleton loader with variants for text, circular, rectangular, and rounded shapes',
    defaultProps: {
      width: 200,
      height: 20,
      variant: 'text',
      animate: true,
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'width',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Width of the skeleton',
        defaultValue: 200,
      },
      {
        name: 'height',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Height of the skeleton',
        defaultValue: 20,
      },
      {
        name: 'variant',
        type: 'string',
        control: { type: 'select', options: ['text', 'circular', 'rectangular', 'rounded'] },
        description: 'Shape variant of the skeleton',
        defaultValue: 'text',
      },
      {
        name: 'animate',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Enable shimmer animation',
        defaultValue: true,
      },
      {
        name: 'baseColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Base background color',
        defaultValue: '#e0e0e0',
      },
      {
        name: 'highlightColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Shimmer highlight color',
        defaultValue: '#f5f5f5',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Text', props: { variant: 'text', width: 300, height: 16 } },
      { name: 'Circular', props: { variant: 'circular', width: 60, height: 60 } },
      { name: 'Rectangular', props: { variant: 'rectangular', width: 300, height: 200 } },
    ],
    importPath: "import { Skeleton } from 'premium-react-loaders';",
  },
  {
    id: 'skeleton-text',
    name: 'SkeletonText',
    category: 'skeleton',
    component: SkeletonText,
    description: 'Multi-line text placeholder with customizable last line width',
    defaultProps: {
      lines: 3,
      width: '100%',
      gap: '0.5rem',
      lastLineWidth: '80%',
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'lines',
        type: 'number',
        control: { type: 'range', min: 1, max: 10, step: 1 },
        description: 'Number of skeleton lines',
        defaultValue: 3,
      },
      {
        name: 'width',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Width of skeleton lines',
        defaultValue: '100%',
      },
      {
        name: 'lastLineWidth',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Width of the last line (defaults to 80%)',
        defaultValue: '80%',
      },
      {
        name: 'gap',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Gap between lines',
        defaultValue: '0.5rem',
      },
      {
        name: 'animate',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Enable shimmer animation',
        defaultValue: true,
      },
      {
        name: 'baseColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Base background color',
        defaultValue: '#e0e0e0',
      },
      {
        name: 'highlightColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Shimmer highlight color',
        defaultValue: '#f5f5f5',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Single Line', props: { lines: 1 } },
      { name: 'Many Lines', props: { lines: 5 } },
      { name: 'Custom Last Line', props: { lines: 4, lastLineWidth: '60%' } },
    ],
    importPath: "import { SkeletonText } from 'premium-react-loaders';",
  },
  {
    id: 'skeleton-avatar',
    name: 'SkeletonAvatar',
    category: 'skeleton',
    component: SkeletonAvatar,
    description: 'Avatar placeholder with circle or square shape options',
    defaultProps: {
      size: 48,
      shape: 'circle',
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'number',
        control: { type: 'range', min: 24, max: 128, step: 8 },
        description: 'Size of the avatar',
        defaultValue: 48,
      },
      {
        name: 'shape',
        type: 'string',
        control: { type: 'select', options: ['circle', 'square'] },
        description: 'Shape of the avatar',
        defaultValue: 'circle',
      },
      {
        name: 'animate',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Enable shimmer animation',
        defaultValue: true,
      },
      {
        name: 'baseColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Base background color',
        defaultValue: '#e0e0e0',
      },
      {
        name: 'highlightColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Shimmer highlight color',
        defaultValue: '#f5f5f5',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Small Circle', props: { size: 32, shape: 'circle' } },
      { name: 'Large Square', props: { size: 64, shape: 'square' } },
    ],
    importPath: "import { SkeletonAvatar } from 'premium-react-loaders';",
  },
  {
    id: 'skeleton-image',
    name: 'SkeletonImage',
    category: 'skeleton',
    component: SkeletonImage,
    description: 'Image placeholder with aspect ratio support',
    defaultProps: {
      width: 300,
      height: 200,
      aspectRatio: '16/9',
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'width',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Width of the image skeleton',
        defaultValue: 300,
      },
      {
        name: 'height',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Height of the image skeleton',
        defaultValue: 200,
      },
      {
        name: 'aspectRatio',
        type: 'string',
        control: { type: 'select', options: ['16/9', '4/3', '1/1', '3/4'] },
        description: 'Aspect ratio of the image',
        defaultValue: '16/9',
      },
      {
        name: 'animate',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Enable shimmer animation',
        defaultValue: true,
      },
      {
        name: 'baseColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Base background color',
        defaultValue: '#e0e0e0',
      },
      {
        name: 'highlightColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Shimmer highlight color',
        defaultValue: '#f5f5f5',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Square', props: { aspectRatio: '1/1', width: 200 } },
      { name: 'Portrait', props: { aspectRatio: '3/4', width: 200 } },
    ],
    importPath: "import { SkeletonImage } from 'premium-react-loaders';",
  },
  {
    id: 'skeleton-card',
    name: 'SkeletonCard',
    category: 'skeleton',
    component: SkeletonCard,
    description: 'Card layout skeleton with optional avatar and multiple text lines',
    defaultProps: {
      hasAvatar: true,
      lines: 3,
      titleWidth: '80%',
      avatarSize: 48,
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'hasAvatar',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show avatar in card',
        defaultValue: true,
      },
      {
        name: 'lines',
        type: 'number',
        control: { type: 'range', min: 1, max: 5, step: 1 },
        description: 'Number of description lines',
        defaultValue: 3,
      },
      {
        name: 'titleWidth',
        type: 'string',
        control: { type: 'text' },
        description: 'Width of the title line',
        defaultValue: '80%',
      },
      {
        name: 'avatarSize',
        type: 'number',
        control: { type: 'range', min: 32, max: 80, step: 8 },
        description: 'Size of the avatar',
        defaultValue: 48,
      },
      {
        name: 'animate',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Enable shimmer animation',
        defaultValue: true,
      },
      {
        name: 'baseColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Base background color',
        defaultValue: '#e0e0e0',
      },
      {
        name: 'highlightColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Shimmer highlight color',
        defaultValue: '#f5f5f5',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Without Avatar', props: { hasAvatar: false } },
      { name: 'Large Avatar', props: { avatarSize: 64, lines: 2 } },
    ],
    importPath: "import { SkeletonCard } from 'premium-react-loaders';",
  },
  {
    id: 'skeleton-list',
    name: 'SkeletonList',
    category: 'skeleton',
    component: SkeletonList,
    description: 'List of skeleton items with customizable count',
    defaultProps: {
      items: 5,
      gap: 12,
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'items',
        type: 'number',
        control: { type: 'range', min: 1, max: 10, step: 1 },
        description: 'Number of list items',
        defaultValue: 5,
      },
      {
        name: 'gap',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Gap between items',
        defaultValue: 12,
      },
      {
        name: 'itemHeight',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Height of each item',
        defaultValue: '3rem',
      },
      {
        name: 'animate',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Enable shimmer animation',
        defaultValue: true,
      },
      {
        name: 'baseColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Base background color',
        defaultValue: '#e0e0e0',
      },
      {
        name: 'highlightColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Shimmer highlight color',
        defaultValue: '#f5f5f5',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Few Items', props: { items: 3 } },
      { name: 'Many Items', props: { items: 8, gap: 12 } },
    ],
    importPath: "import { SkeletonList } from 'premium-react-loaders';",
  },
  {
    id: 'skeleton-table',
    name: 'SkeletonTable',
    category: 'skeleton',
    component: SkeletonTable,
    description: 'Table skeleton with configurable rows and columns',
    defaultProps: {
      rows: 5,
      columns: 4,
      showHeader: true,
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'rows',
        type: 'number',
        control: { type: 'range', min: 1, max: 10, step: 1 },
        description: 'Number of table rows',
        defaultValue: 5,
      },
      {
        name: 'columns',
        type: 'number',
        control: { type: 'range', min: 2, max: 6, step: 1 },
        description: 'Number of table columns',
        defaultValue: 4,
      },
      {
        name: 'showHeader',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show table header row',
        defaultValue: true,
      },
      {
        name: 'animate',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Enable shimmer animation',
        defaultValue: true,
      },
      {
        name: 'baseColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Base background color',
        defaultValue: '#e0e0e0',
      },
      {
        name: 'highlightColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Shimmer highlight color',
        defaultValue: '#f5f5f5',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Small Table', props: { rows: 3, columns: 3 } },
      { name: 'Large Table', props: { rows: 8, columns: 5 } },
      { name: 'No Header', props: { showHeader: false } },
    ],
    importPath: "import { SkeletonTable } from 'premium-react-loaders';",
  },
  {
    id: 'skeleton-page',
    name: 'SkeletonPage',
    category: 'skeleton',
    component: SkeletonPage,
    description: 'Pre-built full page skeleton layouts for common page types',
    defaultProps: {
      variant: 'default',
      animate: true,
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'variant',
        type: 'string',
        control: { type: 'select', options: ['default', 'dashboard', 'article', 'profile'] },
        description: 'Page layout variant',
        defaultValue: 'default',
      },
      {
        name: 'animate',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Enable shimmer animation',
        defaultValue: true,
      },
      {
        name: 'baseColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Base background color',
        defaultValue: '#e0e0e0',
      },
      {
        name: 'highlightColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Shimmer highlight color',
        defaultValue: '#f5f5f5',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Dashboard', props: { variant: 'dashboard' } },
      { name: 'Article', props: { variant: 'article' } },
      { name: 'Profile', props: { variant: 'profile' } },
      { name: 'No Animation', props: { animate: false } },
    ],
    importPath: "import { SkeletonPage } from 'premium-react-loaders';",
  },
  {
    id: 'skeleton-form',
    name: 'SkeletonForm',
    category: 'skeleton',
    component: SkeletonForm,
    description: 'Form loading skeleton with configurable fields, labels, and submit button',
    defaultProps: {
      fields: 3,
      showLabels: true,
      showButton: true,
      gap: 16,
      buttonWidth: '120px',
      buttonPosition: 'left',
      animate: true,
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'fields',
        type: 'number',
        control: { type: 'range', min: 1, max: 10, step: 1 },
        description: 'Number of form fields',
        defaultValue: 3,
      },
      {
        name: 'showLabels',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show field labels',
        defaultValue: true,
      },
      {
        name: 'showButton',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show submit button',
        defaultValue: true,
      },
      {
        name: 'gap',
        type: 'number | string',
        control: { type: 'range', min: 8, max: 32, step: 4 },
        description: 'Spacing between fields',
        defaultValue: 16,
      },
      {
        name: 'buttonWidth',
        type: 'string',
        control: { type: 'text' },
        description: 'Button width',
        defaultValue: '120px',
      },
      {
        name: 'buttonPosition',
        type: 'string',
        control: { type: 'select', options: ['left', 'right', 'center'] },
        description: 'Button alignment',
        defaultValue: 'left',
      },
      {
        name: 'animate',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Enable shimmer animation',
        defaultValue: true,
      },
      {
        name: 'baseColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Base background color',
        defaultValue: '#e0e0e0',
      },
      {
        name: 'highlightColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Shimmer highlight color',
        defaultValue: '#f5f5f5',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Many Fields', props: { fields: 5 } },
      { name: 'Without Labels', props: { showLabels: false, fields: 4 } },
      { name: 'Without Button', props: { showButton: false } },
      { name: 'Right Aligned Button', props: { buttonPosition: 'right' } },
    ],
    importPath: "import { SkeletonForm } from 'premium-react-loaders';",
  },

  // ==================== SPINNER COMPONENTS ====================
  {
    id: 'spinner-circle',
    name: 'SpinnerCircle',
    category: 'spinner',
    component: SpinnerCircle,
    description: 'Basic rotating circle spinner with partial arc',
    defaultProps: {
      size: 40,
      color: '#3b82f6',
      thickness: 4,
      speed: 'normal',
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'number',
        control: { type: 'range', min: 16, max: 80, step: 4 },
        description: 'Size of the spinner',
        defaultValue: 40,
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Color of the spinner',
        defaultValue: '#3b82f6',
      },
      {
        name: 'thickness',
        type: 'number',
        control: { type: 'range', min: 1, max: 8, step: 1 },
        description: 'Thickness of the spinner',
        defaultValue: 4,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Small', props: { size: 24 } },
      { name: 'Large', props: { size: 60 } },
      { name: 'Custom Color', props: { color: '#8b5cf6', size: 40 } },
      { name: 'Thick', props: { thickness: 6, size: 48 } },
      { name: 'Fast', props: { speed: 'fast', size: 40 } },
    ],
    importPath: "import { SpinnerCircle } from 'premium-react-loaders';",
  },
  {
    id: 'spinner-ring',
    name: 'SpinnerRing',
    category: 'spinner',
    component: SpinnerRing,
    description: 'Border-only ring spinner with smooth rotation and customizable background',
    defaultProps: {
      size: 40,
      color: '#3b82f6',
      secondaryColor: 'rgba(0, 0, 0, 0.1)',
      thickness: 4,
      speed: 'normal',
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'number',
        control: { type: 'range', min: 16, max: 80, step: 4 },
        description: 'Size of the spinner',
        defaultValue: 40,
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Color of the spinner ring',
        defaultValue: '#3b82f6',
      },
      {
        name: 'thickness',
        type: 'number',
        control: { type: 'range', min: 1, max: 8, step: 1 },
        description: 'Thickness of the ring',
        defaultValue: 4,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
      {
        name: 'secondaryColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Background ring color',
        defaultValue: 'rgba(0, 0, 0, 0.1)',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Small', props: { size: 24 } },
      { name: 'Large', props: { size: 60 } },
      { name: 'Custom Color', props: { color: '#ec4899', size: 40 } },
      { name: 'Custom Secondary Color', props: { color: '#3b82f6', secondaryColor: '#e0e0e0', size: 40 } },
    ],
    importPath: "import { SpinnerRing } from 'premium-react-loaders';",
  },
  {
    id: 'spinner-dots',
    name: 'SpinnerDots',
    category: 'spinner',
    component: SpinnerDots,
    description: 'Multiple dots rotating around center',
    defaultProps: {
      size: 40,
      dotCount: 8,
      dotSize: 6,
      color: '#3b82f6',
      speed: 'normal',
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'number',
        control: { type: 'range', min: 24, max: 80, step: 4 },
        description: 'Size of the spinner container',
        defaultValue: 40,
      },
      {
        name: 'dotCount',
        type: 'number',
        control: { type: 'range', min: 4, max: 12, step: 1 },
        description: 'Number of dots',
        defaultValue: 8,
      },
      {
        name: 'dotSize',
        type: 'number',
        control: { type: 'range', min: 2, max: 12, step: 1 },
        description: 'Size of each dot',
        defaultValue: 6,
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Color of the dots',
        defaultValue: '#3b82f6',
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Few Dots', props: { dotCount: 6 } },
      { name: 'Many Dots', props: { dotCount: 12, dotSize: 4 } },
    ],
    importPath: "import { SpinnerDots } from 'premium-react-loaders';",
  },
  {
    id: 'spinner-bars',
    name: 'SpinnerBars',
    category: 'spinner',
    component: SpinnerBars,
    description: 'Vertical bars with wave animation',
    defaultProps: {
      size: 40,
      barCount: 5,
      color: '#3b82f6',
      speed: 'normal',
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'number',
        control: { type: 'range', min: 24, max: 80, step: 4 },
        description: 'Height of the spinner',
        defaultValue: 40,
      },
      {
        name: 'barCount',
        type: 'number',
        control: { type: 'range', min: 3, max: 7, step: 1 },
        description: 'Number of bars',
        defaultValue: 5,
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Color of the bars',
        defaultValue: '#3b82f6',
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Few Bars', props: { barCount: 3 } },
      { name: 'Many Bars', props: { barCount: 7 } },
    ],
    importPath: "import { SpinnerBars } from 'premium-react-loaders';",
  },
  {
    id: 'spinner-grid',
    name: 'SpinnerGrid',
    category: 'spinner',
    component: SpinnerGrid,
    description: 'Grid of fading squares',
    defaultProps: {
      size: 40,
      gridSize: 3,
      color: '#3b82f6',
      speed: 'normal',
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'number',
        control: { type: 'range', min: 24, max: 80, step: 4 },
        description: 'Size of the grid',
        defaultValue: 40,
      },
      {
        name: 'gridSize',
        type: 'number',
        control: { type: 'range', min: 2, max: 5, step: 1 },
        description: 'Grid size (e.g., 3 for 3x3)',
        defaultValue: 3,
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Color of the squares',
        defaultValue: '#3b82f6',
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Small 2x2', props: { size: 24, gridSize: 2 } },
      { name: 'Large 4x4', props: { size: 60, gridSize: 4 } },
    ],
    importPath: "import { SpinnerGrid } from 'premium-react-loaders';",
  },
  {
    id: 'spinner-wave',
    name: 'SpinnerWave',
    category: 'spinner',
    component: SpinnerWave,
    description: 'Ripple wave spinner with expanding circles',
    defaultProps: {
      size: 40,
      color: '#3b82f6',
      ripples: 3,
      maxScale: 2,
      speed: 'normal',
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'number',
        control: { type: 'range', min: 20, max: 100, step: 10 },
        description: 'Size of the spinner',
        defaultValue: 40,
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Color of the ripples',
        defaultValue: '#3b82f6',
      },
      {
        name: 'ripples',
        type: 'number',
        control: { type: 'range', min: 1, max: 5, step: 1 },
        description: 'Number of expanding ripples',
        defaultValue: 3,
      },
      {
        name: 'maxScale',
        type: 'number',
        control: { type: 'range', min: 1.5, max: 3, step: 0.1 },
        description: 'Maximum scale before fade',
        defaultValue: 2,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Single Ripple', props: { ripples: 1 } },
      { name: 'Many Ripples', props: { ripples: 5 } },
      { name: 'Large Size', props: { size: 80 } },
      { name: 'Fast Speed', props: { speed: 'fast', size: 50 } },
    ],
    importPath: "import { SpinnerWave } from 'premium-react-loaders';",
  },
  {
    id: 'spinner-pulse',
    name: 'SpinnerPulse',
    category: 'spinner',
    component: SpinnerPulse,
    description: 'Heartbeat pulse spinner for critical operations',
    defaultProps: {
      size: 40,
      color: '#3b82f6',
      pulses: 2,
      maxScale: 1.8,
      speed: 'normal',
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'number',
        control: { type: 'range', min: 20, max: 100, step: 10 },
        description: 'Size of the spinner',
        defaultValue: 40,
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Color of the pulse',
        defaultValue: '#3b82f6',
      },
      {
        name: 'pulses',
        type: 'number',
        control: { type: 'range', min: 1, max: 3, step: 1 },
        description: 'Number of pulse circles',
        defaultValue: 2,
      },
      {
        name: 'maxScale',
        type: 'number',
        control: { type: 'range', min: 1.2, max: 2.5, step: 0.1 },
        description: 'Maximum scale at expansion',
        defaultValue: 1.8,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Single Pulse', props: { pulses: 1 } },
      { name: 'Triple Pulse', props: { pulses: 3 } },
      { name: 'Large with Slow Speed', props: { size: 80, speed: 'slow' } },
      { name: 'Fast Speed', props: { speed: 'fast', size: 50 } },
    ],
    importPath: "import { SpinnerPulse } from 'premium-react-loaders';",
  },

  // ==================== PROGRESS COMPONENTS ====================
  {
    id: 'progress-bar',
    name: 'ProgressBar',
    category: 'progress',
    component: ProgressBar,
    description: 'Linear horizontal progress bar with buffer, gradient, and speed control',
    defaultProps: {
      value: 75,
      showValue: false,
      indeterminate: false,
      height: 8,
      color: '#3b82f6',
      gradient: false,
      speed: 'normal',
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'value',
        type: 'number',
        control: { type: 'range', min: 0, max: 100, step: 1 },
        description: 'Progress value (0-100)',
        defaultValue: 75,
      },
      {
        name: 'buffer',
        type: 'number',
        control: { type: 'range', min: 0, max: 100, step: 1 },
        description: 'Buffer value for YouTube-style loading (0-100)',
        defaultValue: undefined,
      },
      {
        name: 'indeterminate',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Indeterminate mode',
        defaultValue: false,
      },
      {
        name: 'showValue',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show percentage text',
        defaultValue: false,
      },
      {
        name: 'gradient',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Enable gradient effect',
        defaultValue: false,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed for indeterminate mode',
        defaultValue: 'normal',
      },
      {
        name: 'height',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Height of the progress bar',
        defaultValue: 8,
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Progress color',
        defaultValue: '#3b82f6',
      },
      {
        name: 'secondaryColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Background/gradient end color',
        defaultValue: '#e0e0e0',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: { value: 75 } },
      { name: 'With Value', props: { value: 65, showValue: true } },
      { name: 'With Buffer', props: { value: 50, buffer: 75 } },
      { name: 'With Gradient', props: { value: 60, gradient: true, secondaryColor: '#8b5cf6' } },
      { name: 'Indeterminate Fast', props: { indeterminate: true, speed: 'fast' } },
      { name: 'Thick', props: { value: 60, height: 12, showValue: true } },
    ],
    importPath: "import { ProgressBar } from 'premium-react-loaders';",
  },
  {
    id: 'progress-circle',
    name: 'ProgressCircle',
    category: 'progress',
    component: ProgressCircle,
    description: 'Circular progress indicator with buffer and speed control',
    defaultProps: {
      value: 75,
      size: 80,
      thickness: 4,
      showValue: false,
      indeterminate: false,
      color: '#3b82f6',
      speed: 'normal',
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'value',
        type: 'number',
        control: { type: 'range', min: 0, max: 100, step: 1 },
        description: 'Progress value (0-100)',
        defaultValue: 75,
      },
      {
        name: 'buffer',
        type: 'number',
        control: { type: 'range', min: 0, max: 100, step: 1 },
        description: 'Buffer value for YouTube-style loading (0-100)',
        defaultValue: undefined,
      },
      {
        name: 'size',
        type: 'number',
        control: { type: 'range', min: 40, max: 120, step: 10 },
        description: 'Size of the circle',
        defaultValue: 80,
      },
      {
        name: 'thickness',
        type: 'number',
        control: { type: 'range', min: 1, max: 12, step: 1 },
        description: 'Thickness of the ring',
        defaultValue: 4,
      },
      {
        name: 'showValue',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show percentage text',
        defaultValue: false,
      },
      {
        name: 'indeterminate',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Indeterminate mode',
        defaultValue: false,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed for indeterminate mode',
        defaultValue: 'normal',
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Progress color',
        defaultValue: '#3b82f6',
      },
      {
        name: 'secondaryColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Background color',
        defaultValue: '#e0e0e0',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: { value: 75 } },
      { name: 'With Value', props: { value: 60, showValue: true } },
      { name: 'With Buffer', props: { value: 50, buffer: 75, showValue: true } },
      { name: 'Indeterminate Fast', props: { indeterminate: true, speed: 'fast' } },
      { name: 'Large', props: { value: 80, size: 100, showValue: true } },
    ],
    importPath: "import { ProgressCircle } from 'premium-react-loaders';",
  },
  {
    id: 'progress-ring',
    name: 'ProgressRing',
    category: 'progress',
    component: ProgressRing,
    description: 'Ring progress indicator with gradient, buffer, and speed control',
    defaultProps: {
      value: 75,
      size: 80,
      thickness: 4,
      showValue: false,
      gradient: false,
      color: '#3b82f6',
      speed: 'normal',
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'value',
        type: 'number',
        control: { type: 'range', min: 0, max: 100, step: 1 },
        description: 'Progress value (0-100)',
        defaultValue: 75,
      },
      {
        name: 'buffer',
        type: 'number',
        control: { type: 'range', min: 0, max: 100, step: 1 },
        description: 'Buffer value for YouTube-style loading (0-100)',
        defaultValue: undefined,
      },
      {
        name: 'size',
        type: 'number',
        control: { type: 'range', min: 40, max: 120, step: 10 },
        description: 'Size of the ring',
        defaultValue: 80,
      },
      {
        name: 'thickness',
        type: 'number',
        control: { type: 'range', min: 2, max: 16, step: 2 },
        description: 'Thickness of the ring',
        defaultValue: 4,
      },
      {
        name: 'showValue',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show percentage text',
        defaultValue: false,
      },
      {
        name: 'gradient',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Use gradient effect',
        defaultValue: false,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed for indeterminate mode',
        defaultValue: 'normal',
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Progress color',
        defaultValue: '#3b82f6',
      },
      {
        name: 'secondaryColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Background/gradient end color',
        defaultValue: '#e0e0e0',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: { value: 75 } },
      { name: 'With Gradient', props: { value: 80, gradient: true, secondaryColor: '#8b5cf6' } },
      { name: 'With Buffer', props: { value: 50, buffer: 75, showValue: true } },
      { name: 'Thick Ring', props: { value: 65, thickness: 12 } },
    ],
    importPath: "import { ProgressRing } from 'premium-react-loaders';",
  },
  {
    id: 'progress-steps',
    name: 'ProgressSteps',
    category: 'progress',
    component: ProgressSteps,
    description: 'Multi-step progress indicator for wizards, forms, and workflows',
    defaultProps: {
      steps: 4,
      currentStep: 1,
      showNumbers: true,
      orientation: 'horizontal',
      connector: 'line',
      color: '#3b82f6',
      inactiveColor: '#e0e0e0',
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'steps',
        type: 'number',
        control: { type: 'range', min: 2, max: 10, step: 1 },
        description: 'Total number of steps',
        defaultValue: 4,
      },
      {
        name: 'currentStep',
        type: 'number',
        control: { type: 'range', min: 0, max: 9, step: 1 },
        description: 'Current active step (0-indexed)',
        defaultValue: 1,
      },
      {
        name: 'showNumbers',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show step numbers',
        defaultValue: true,
      },
      {
        name: 'orientation',
        type: 'string',
        control: { type: 'select', options: ['horizontal', 'vertical'] },
        description: 'Layout orientation',
        defaultValue: 'horizontal',
      },
      {
        name: 'connector',
        type: 'string',
        control: { type: 'select', options: ['line', 'none'] },
        description: 'Connector style',
        defaultValue: 'line',
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Primary color',
        defaultValue: '#3b82f6',
      },
      {
        name: 'completedColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Color for completed steps',
        defaultValue: undefined,
      },
      {
        name: 'activeColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Color for active step',
        defaultValue: undefined,
      },
      {
        name: 'inactiveColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Color for inactive steps',
        defaultValue: '#e0e0e0',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader (prevents flash on fast loads)',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader once visible',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition (true for 150ms default)',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: { steps: 4, currentStep: 1 } },
      { name: 'With Labels', props: { steps: 5, currentStep: 2, labels: ['Personal', 'Address', 'Payment', 'Review', 'Confirm'] } },
      { name: 'Vertical', props: { steps: 4, currentStep: 1, orientation: 'vertical', labels: ['Start', 'Processing', 'Review', 'Complete'] } },
      { name: 'Without Connectors', props: { steps: 4, currentStep: 2, connector: 'none' } },
      { name: 'Custom Colors', props: { steps: 5, currentStep: 2, completedColor: '#10b981', activeColor: '#3b82f6' } },
    ],
    importPath: "import { ProgressSteps } from 'premium-react-loaders';",
  },

  // ==================== PULSE COMPONENTS ====================
  {
    id: 'pulse-dots',
    name: 'PulseDots',
    category: 'pulse',
    component: PulseDots,
    description: 'Bouncing dots in sequence',
    defaultProps: {
      dotCount: 3,
      dotSize: 12,
      size: 60,
      color: '#3b82f6',
      speed: 'normal',
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'number',
        control: { type: 'range', min: 30, max: 100, step: 10 },
        description: 'Height of the container',
        defaultValue: 60,
      },
      {
        name: 'dotCount',
        type: 'number',
        control: { type: 'range', min: 2, max: 5, step: 1 },
        description: 'Number of dots',
        defaultValue: 3,
      },
      {
        name: 'dotSize',
        type: 'number',
        control: { type: 'range', min: 6, max: 20, step: 2 },
        description: 'Size of each dot',
        defaultValue: 12,
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Color of the dots',
        defaultValue: '#3b82f6',
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Few Dots', props: { dotCount: 2 } },
      { name: 'Many Dots', props: { dotCount: 5 } },
      { name: 'Large Dots', props: { dotSize: 16, size: 80 } },
    ],
    importPath: "import { PulseDots } from 'premium-react-loaders';",
  },
  {
    id: 'pulse-wave',
    name: 'PulseWave',
    category: 'pulse',
    component: PulseWave,
    description: 'Wave pattern bars',
    defaultProps: {
      barCount: 5,
      size: 40,
      color: '#3b82f6',
      speed: 'normal',
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'number',
        control: { type: 'range', min: 24, max: 80, step: 4 },
        description: 'Height of the wave',
        defaultValue: 40,
      },
      {
        name: 'barCount',
        type: 'number',
        control: { type: 'range', min: 3, max: 7, step: 1 },
        description: 'Number of wave bars',
        defaultValue: 5,
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Color of the wave',
        defaultValue: '#3b82f6',
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Few Bars', props: { barCount: 3 } },
      { name: 'Many Bars', props: { barCount: 7 } },
    ],
    importPath: "import { PulseWave } from 'premium-react-loaders';",
  },
  {
    id: 'pulse-bars',
    name: 'PulseBars',
    category: 'pulse',
    component: PulseBars,
    description: 'Equalizer-style pulsing bars',
    defaultProps: {
      barCount: 4,
      size: 40,
      color: '#3b82f6',
      speed: 'normal',
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'number',
        control: { type: 'range', min: 24, max: 80, step: 4 },
        description: 'Height of the bars',
        defaultValue: 40,
      },
      {
        name: 'barCount',
        type: 'number',
        control: { type: 'range', min: 3, max: 6, step: 1 },
        description: 'Number of bars',
        defaultValue: 4,
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Color of the bars',
        defaultValue: '#3b82f6',
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Few Bars', props: { barCount: 3 } },
      { name: 'Fast', props: { speed: 'fast' } },
    ],
    importPath: "import { PulseBars } from 'premium-react-loaders';",
  },
  {
    id: 'typing-indicator',
    name: 'TypingIndicator',
    category: 'pulse',
    component: TypingIndicator,
    description: 'Chat typing indicator with bounce and fade animations',
    defaultProps: {
      dotCount: 3,
      dotSize: 8,
      gap: 4,
      color: '#3b82f6',
      variant: 'bounce',
      speed: 'normal',
    },
    propDefinitions: [
      {
        name: 'dotCount',
        type: 'number',
        control: { type: 'range', min: 2, max: 6, step: 1 },
        description: 'Number of dots',
        defaultValue: 3,
      },
      {
        name: 'dotSize',
        type: 'number',
        control: { type: 'range', min: 4, max: 16, step: 2 },
        description: 'Size of each dot',
        defaultValue: 8,
      },
      {
        name: 'gap',
        type: 'number',
        control: { type: 'range', min: 2, max: 12, step: 2 },
        description: 'Space between dots',
        defaultValue: 4,
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Color of the dots',
        defaultValue: '#3b82f6',
      },
      {
        name: 'variant',
        type: 'string',
        control: { type: 'select', options: ['bounce', 'fade'] },
        description: 'Animation style',
        defaultValue: 'bounce',
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Fade Variant', props: { variant: 'fade' } },
      { name: 'Large Dots', props: { dotSize: 12, gap: 6 } },
      { name: 'Many Dots', props: { dotCount: 5 } },
      { name: 'Fast Speed', props: { speed: 'fast' } },
    ],
    importPath: "import { TypingIndicator } from 'premium-react-loaders';",
  },

  // ==================== OVERLAY COMPONENTS ====================
  {
    id: 'loader-overlay',
    name: 'LoaderOverlay',
    category: 'overlay',
    component: LoaderOverlay,
    description: 'Overlay wrapper for displaying loaders over content with backdrop',
    defaultProps: {
      loading: false,
      position: 'fixed',
      backdropOpacity: 0.5,
      backdropColor: '#000000',
      blur: false,
      zIndex: 9999,
    },
    propDefinitions: [
      {
        name: 'loading',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Whether the overlay is active/loading',
        defaultValue: true,
      },
      {
        name: 'position',
        type: 'string',
        control: { type: 'select', options: ['fixed', 'absolute'] },
        description: 'Position style of the overlay',
        defaultValue: 'fixed',
      },
      {
        name: 'backdropOpacity',
        type: 'number',
        control: { type: 'range', min: 0, max: 1, step: 0.1 },
        description: 'Backdrop opacity (0-1)',
        defaultValue: 0.5,
      },
      {
        name: 'backdropColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Backdrop color',
        defaultValue: '#000000',
      },
      {
        name: 'blur',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Apply blur effect to backdrop',
        defaultValue: false,
      },
      {
        name: 'zIndex',
        type: 'number',
        control: { type: 'number' },
        description: 'Z-index of the overlay',
        defaultValue: 9999,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'With Blur', props: { blur: true } },
      { name: 'Light Backdrop', props: { backdropColor: '#ffffff', backdropOpacity: 0.8 } },
      { name: 'Heavy Opacity', props: { backdropOpacity: 0.9 } },
      { name: 'Absolute Position', props: { position: 'absolute' } },
    ],
    importPath: "import { LoaderOverlay } from 'premium-react-loaders';",
  },

  // ==================== BUTTON COMPONENTS ====================
  {
    id: 'button-spinner',
    name: 'ButtonSpinner',
    category: 'button',
    component: ButtonSpinner,
    description: 'Compact spinner designed for button loading states with positioning options',
    defaultProps: {
      size: 16,
      color: 'currentColor',
      variant: 'circle',
      position: 'left',
      thickness: 2,
      dotCount: 3,
      barCount: 3,
      speed: 'normal',
      gap: 8,
      showContent: true,
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'variant',
        type: 'string',
        control: { type: 'select', options: ['circle', 'dots', 'bars'] },
        description: 'Visual variant of the spinner',
        defaultValue: 'circle',
      },
      {
        name: 'position',
        type: 'string',
        control: { type: 'select', options: ['left', 'right', 'center'] },
        description: 'Position relative to content',
        defaultValue: 'left',
      },
      {
        name: 'size',
        type: 'number',
        control: { type: 'range', min: 8, max: 32, step: 2 },
        description: 'Size of the spinner in pixels',
        defaultValue: 16,
      },
      {
        name: 'showContent',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Whether to show content when loading',
        defaultValue: true,
      },
      {
        name: 'gap',
        type: 'number',
        control: { type: 'range', min: 0, max: 24, step: 2 },
        description: 'Gap between spinner and content',
        defaultValue: 8,
      },
      {
        name: 'thickness',
        type: 'number',
        control: { type: 'range', min: 1, max: 4, step: 0.5 },
        description: 'Thickness (circle variant)',
        defaultValue: 2,
      },
      {
        name: 'dotCount',
        type: 'number',
        control: { type: 'range', min: 2, max: 5, step: 1 },
        description: 'Number of dots (dots variant)',
        defaultValue: 3,
      },
      {
        name: 'barCount',
        type: 'number',
        control: { type: 'range', min: 2, max: 5, step: 1 },
        description: 'Number of bars (bars variant)',
        defaultValue: 3,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Circle Variant', props: { variant: 'circle', children: 'Submit' } },
      { name: 'Dots Variant', props: { variant: 'dots', children: 'Loading' } },
      { name: 'Bars Variant', props: { variant: 'bars', children: 'Processing' } },
      { name: 'Right Position', props: { position: 'right', children: 'Next' } },
      { name: 'Hide Content', props: { showContent: false, children: 'Click Me' } },
    ],
    importPath: "import { ButtonSpinner } from 'premium-react-loaders';",
  },

  // ==================== STATUS COMPONENTS ====================
  {
    id: 'success-checkmark',
    name: 'SuccessCheckmark',
    category: 'status',
    component: SuccessCheckmark,
    description: 'Animated checkmark for successful operations with optional circle background',
    defaultProps: {
      size: 'md',
      color: '#10b981',
      strokeWidth: 3,
      duration: 500,
      showCircle: false,
      fillCircle: false,
      delay: 0,
      minDuration: 0,
      transition: true,
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Size of the checkmark',
        defaultValue: 'md',
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Color of the checkmark',
        defaultValue: '#10b981',
      },
      {
        name: 'strokeWidth',
        type: 'number',
        control: { type: 'range', min: 1, max: 6, step: 0.5 },
        description: 'Stroke width of the checkmark',
        defaultValue: 3,
      },
      {
        name: 'duration',
        type: 'number',
        control: { type: 'range', min: 200, max: 1500, step: 50 },
        description: 'Animation duration in milliseconds',
        defaultValue: 500,
      },
      {
        name: 'showCircle',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show circle background',
        defaultValue: false,
      },
      {
        name: 'fillCircle',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Fill the circle background',
        defaultValue: false,
      },
      {
        name: 'circleColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Circle background color',
        defaultValue: undefined,
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition',
        defaultValue: true,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'With Circle', props: { showCircle: true, size: 64 } },
      { name: 'Filled Circle', props: { showCircle: true, fillCircle: true, size: 64 } },
      { name: 'Custom Color', props: { color: '#22c55e', showCircle: true, size: 64 } },
      { name: 'Fast Animation', props: { duration: 300, showCircle: true, size: 64 } },
    ],
    importPath: "import { SuccessCheckmark } from 'premium-react-loaders';",
  },
  {
    id: 'error-indicator',
    name: 'ErrorIndicator',
    category: 'status',
    component: ErrorIndicator,
    description: 'Animated error indicator with X mark and optional shake animation',
    defaultProps: {
      size: 'md',
      color: '#ef4444',
      strokeWidth: 3,
      duration: 500,
      showCircle: false,
      fillCircle: false,
      shake: true,
      delay: 0,
      minDuration: 0,
      transition: true,
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Size of the error indicator',
        defaultValue: 'md',
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Color of the X mark',
        defaultValue: '#ef4444',
      },
      {
        name: 'strokeWidth',
        type: 'number',
        control: { type: 'range', min: 1, max: 6, step: 0.5 },
        description: 'Stroke width of the X mark',
        defaultValue: 3,
      },
      {
        name: 'duration',
        type: 'number',
        control: { type: 'range', min: 200, max: 1500, step: 50 },
        description: 'Animation duration in milliseconds',
        defaultValue: 500,
      },
      {
        name: 'showCircle',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show circle background',
        defaultValue: false,
      },
      {
        name: 'fillCircle',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Fill the circle background',
        defaultValue: false,
      },
      {
        name: 'circleColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Circle background color',
        defaultValue: undefined,
      },
      {
        name: 'shake',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Add shake animation',
        defaultValue: true,
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show',
        defaultValue: 0,
      },
      {
        name: 'transition',
        type: 'boolean | number',
        control: { type: 'boolean' },
        description: 'Enable fade transition',
        defaultValue: true,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'With Circle', props: { showCircle: true, size: 64 } },
      { name: 'Filled Circle', props: { showCircle: true, fillCircle: true, size: 64, shake: false } },
      { name: 'Custom Color', props: { color: '#dc2626', showCircle: true, size: 64 } },
      { name: 'Without Shake', props: { shake: false, showCircle: true, size: 64 } },
    ],
    importPath: "import { ErrorIndicator } from 'premium-react-loaders';",
  },

  // ==================== TRANSITION COMPONENTS ====================
  {
    id: 'loader-transition',
    name: 'LoaderTransition',
    category: 'transition',
    component: LoaderTransition,
    description: 'Smooth transitions between loading and loaded states with multiple animation types',
    defaultProps: {
      loading: false,
      loadingContent: null,
      transitionType: 'fade',
      duration: 300,
      timing: 'ease-out',
      keepMounted: false,
      exitDelay: 0,
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'loading',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Whether currently in loading state',
        defaultValue: false,
      },
      {
        name: 'transitionType',
        type: 'string',
        control: { type: 'select', options: ['fade', 'slide-up', 'slide-down', 'slide-left', 'slide-right', 'scale', 'none'] },
        description: 'Type of transition animation',
        defaultValue: 'fade',
      },
      {
        name: 'duration',
        type: 'number',
        control: { type: 'range', min: 100, max: 1000, step: 50 },
        description: 'Duration of transition in milliseconds',
        defaultValue: 300,
      },
      {
        name: 'timing',
        type: 'string',
        control: { type: 'select', options: ['ease', 'ease-in', 'ease-out', 'ease-in-out', 'linear', 'spring'] },
        description: 'Timing function for the transition',
        defaultValue: 'ease-out',
      },
      {
        name: 'keepMounted',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Keep loading content mounted',
        defaultValue: false,
      },
      {
        name: 'exitDelay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay before starting transition out',
        defaultValue: 0,
      },
      {
        name: 'delay',
        type: 'number',
        control: { type: 'range', min: 0, max: 2000, step: 100 },
        description: 'Delay in ms before showing loader',
        defaultValue: 0,
      },
      {
        name: 'minDuration',
        type: 'number',
        control: { type: 'range', min: 0, max: 3000, step: 100 },
        description: 'Minimum duration in ms to show loader',
        defaultValue: 0,
      },
    ],
    examples: [
      { name: 'Fade Transition', props: { transitionType: 'fade', duration: 300 } },
      { name: 'Slide Up', props: { transitionType: 'slide-up', duration: 400 } },
      { name: 'Slide Down', props: { transitionType: 'slide-down', duration: 400 } },
      { name: 'Scale', props: { transitionType: 'scale', duration: 350, timing: 'spring' } },
      { name: 'With Delay', props: { transitionType: 'fade', delay: 500 } },
    ],
    importPath: "import { LoaderTransition } from 'premium-react-loaders';",
  },

  // ==================== SHIMMER COMPONENTS ====================
  {
    id: 'shimmer-box',
    name: 'ShimmerBox',
    category: 'shimmer',
    component: ShimmerBox,
    description: 'A versatile shimmer effect container for placeholder content',
    defaultProps: {
      width: 200,
      height: 150,
      borderRadius: 8,
      direction: 'left-to-right',
      speed: 'normal',
    },
    propDefinitions: [
      {
        name: 'width',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Width of the shimmer box',
        defaultValue: 200,
      },
      {
        name: 'height',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Height of the shimmer box',
        defaultValue: 150,
      },
      {
        name: 'borderRadius',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Border radius',
        defaultValue: 8,
      },
      {
        name: 'baseColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Base background color',
        defaultValue: '#e5e7eb',
      },
      {
        name: 'highlightColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Shimmer highlight color',
        defaultValue: '#f3f4f6',
      },
      {
        name: 'direction',
        type: 'string',
        control: { type: 'select', options: ['left-to-right', 'right-to-left', 'top-to-bottom', 'bottom-to-top'] },
        description: 'Direction of shimmer animation',
        defaultValue: 'left-to-right',
      },
      {
        name: 'aspectRatio',
        type: 'string',
        control: { type: 'text' },
        description: 'Aspect ratio (e.g., "16/9")',
        defaultValue: undefined,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Square', props: { width: 100, height: 100 } },
      { name: 'Circle', props: { width: 80, height: 80, borderRadius: '50%' } },
      { name: 'Vertical', props: { width: 150, height: 200, direction: 'top-to-bottom' } },
    ],
    importPath: "import { ShimmerBox } from 'premium-react-loaders';",
  },
  {
    id: 'shimmer-text',
    name: 'ShimmerText',
    category: 'shimmer',
    component: ShimmerText,
    description: 'Multi-line text placeholder with shimmer animation',
    defaultProps: {
      lines: 3,
      width: 300,
      height: 16,
      lineGap: 12,
      lastLineWidth: '75%',
      speed: 'normal',
    },
    propDefinitions: [
      {
        name: 'lines',
        type: 'number',
        control: { type: 'range', min: 1, max: 10, step: 1 },
        description: 'Number of text lines',
        defaultValue: 3,
      },
      {
        name: 'width',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Width of text lines',
        defaultValue: 300,
      },
      {
        name: 'height',
        type: 'number',
        control: { type: 'range', min: 8, max: 32, step: 2 },
        description: 'Height of each line',
        defaultValue: 16,
      },
      {
        name: 'lineGap',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Gap between lines',
        defaultValue: 12,
      },
      {
        name: 'lastLineWidth',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Width of the last line',
        defaultValue: '75%',
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Single Line', props: { lines: 1, width: 200 } },
      { name: 'Paragraph', props: { lines: 5, width: 400, lastLineWidth: '60%' } },
    ],
    importPath: "import { ShimmerText } from 'premium-react-loaders';",
  },
  {
    id: 'shimmer-button',
    name: 'ShimmerButton',
    category: 'shimmer',
    component: ShimmerButton,
    description: 'Button placeholder with shimmer animation',
    defaultProps: {
      width: 120,
      height: 40,
      variant: 'solid',
      showIcon: false,
      iconPosition: 'left',
      speed: 'normal',
    },
    propDefinitions: [
      {
        name: 'width',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Width of the button',
        defaultValue: 120,
      },
      {
        name: 'height',
        type: 'number',
        control: { type: 'range', min: 24, max: 64, step: 4 },
        description: 'Height of the button',
        defaultValue: 40,
      },
      {
        name: 'variant',
        type: 'string',
        control: { type: 'select', options: ['solid', 'outline'] },
        description: 'Button variant style',
        defaultValue: 'solid',
      },
      {
        name: 'showIcon',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show icon placeholder',
        defaultValue: false,
      },
      {
        name: 'iconPosition',
        type: 'string',
        control: { type: 'select', options: ['left', 'right'] },
        description: 'Icon position',
        defaultValue: 'left',
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'With Icon', props: { width: 140, showIcon: true } },
      { name: 'Outline', props: { variant: 'outline' } },
      { name: 'Large', props: { width: 180, height: 52, showIcon: true } },
    ],
    importPath: "import { ShimmerButton } from 'premium-react-loaders';",
  },

  // ==================== ORBIT COMPONENTS ====================
  {
    id: 'orbit-dots',
    name: 'OrbitDots',
    category: 'orbit',
    component: OrbitDots,
    description: 'Dots orbiting around a center point',
    defaultProps: {
      size: 'md',
      color: '#3b82f6',
      dotCount: 4,
      dotSize: 8,
      stagger: true,
      speed: 'normal',
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'string',
        control: { type: 'select', options: ['sm', 'md', 'lg', 'xl'] },
        description: 'Size of the loader',
        defaultValue: 'md',
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Primary color',
        defaultValue: '#3b82f6',
      },
      {
        name: 'secondaryColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Secondary color for alternating dots',
        defaultValue: undefined,
      },
      {
        name: 'dotCount',
        type: 'number',
        control: { type: 'range', min: 2, max: 12, step: 1 },
        description: 'Number of orbiting dots',
        defaultValue: 4,
      },
      {
        name: 'dotSize',
        type: 'number',
        control: { type: 'range', min: 4, max: 16, step: 2 },
        description: 'Size of each dot',
        defaultValue: 8,
      },
      {
        name: 'stagger',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Stagger animation between dots',
        defaultValue: true,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Many Dots', props: { size: 'lg', dotCount: 8, dotSize: 6 } },
      { name: 'Two Color', props: { color: '#3b82f6', secondaryColor: '#ec4899', dotCount: 6 } },
    ],
    importPath: "import { OrbitDots } from 'premium-react-loaders';",
  },
  {
    id: 'orbit-rings',
    name: 'OrbitRings',
    category: 'orbit',
    component: OrbitRings,
    description: 'Concentric rings with rotation animation',
    defaultProps: {
      size: 'md',
      color: '#3b82f6',
      ringCount: 3,
      thickness: 2,
      alternate: true,
      speed: 'normal',
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'string',
        control: { type: 'select', options: ['sm', 'md', 'lg', 'xl'] },
        description: 'Size of the loader',
        defaultValue: 'md',
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Primary color',
        defaultValue: '#3b82f6',
      },
      {
        name: 'secondaryColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Secondary color for alternating rings',
        defaultValue: undefined,
      },
      {
        name: 'ringCount',
        type: 'number',
        control: { type: 'range', min: 1, max: 6, step: 1 },
        description: 'Number of concentric rings',
        defaultValue: 3,
      },
      {
        name: 'thickness',
        type: 'number',
        control: { type: 'range', min: 1, max: 6, step: 1 },
        description: 'Ring thickness',
        defaultValue: 2,
      },
      {
        name: 'alternate',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Alternate rotation direction',
        defaultValue: true,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Four Rings', props: { size: 'lg', ringCount: 4 } },
      { name: 'Two Color', props: { color: '#3b82f6', secondaryColor: '#f59e0b', ringCount: 4 } },
    ],
    importPath: "import { OrbitRings } from 'premium-react-loaders';",
  },
  {
    id: 'atom-loader',
    name: 'AtomLoader',
    category: 'orbit',
    component: AtomLoader,
    description: 'Electron-like orbital animation',
    defaultProps: {
      size: 'md',
      color: '#3b82f6',
      orbits: 3,
      nucleusSize: 8,
      electronSize: 5,
      showNucleus: true,
      speed: 'normal',
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'string',
        control: { type: 'select', options: ['sm', 'md', 'lg', 'xl'] },
        description: 'Size of the loader',
        defaultValue: 'md',
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Primary color',
        defaultValue: '#3b82f6',
      },
      {
        name: 'secondaryColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Secondary color for nucleus',
        defaultValue: undefined,
      },
      {
        name: 'orbits',
        type: 'number',
        control: { type: 'range', min: 1, max: 6, step: 1 },
        description: 'Number of orbital paths',
        defaultValue: 3,
      },
      {
        name: 'nucleusSize',
        type: 'number',
        control: { type: 'range', min: 4, max: 20, step: 2 },
        description: 'Size of the nucleus',
        defaultValue: 8,
      },
      {
        name: 'electronSize',
        type: 'number',
        control: { type: 'range', min: 3, max: 12, step: 1 },
        description: 'Size of electrons',
        defaultValue: 5,
      },
      {
        name: 'showNucleus',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show the nucleus',
        defaultValue: true,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Four Orbits', props: { size: 'lg', orbits: 4 } },
      { name: 'No Nucleus', props: { showNucleus: false } },
    ],
    importPath: "import { AtomLoader } from 'premium-react-loaders';",
  },

  // ==================== BOUNCE COMPONENTS ====================
  {
    id: 'bouncing-dots',
    name: 'BouncingDots',
    category: 'bounce',
    component: BouncingDots,
    description: 'Dots bouncing with physics-based easing',
    defaultProps: {
      color: '#3b82f6',
      dotCount: 3,
      dotSize: 10,
      gap: 8,
      bounceHeight: 1,
      speed: 'normal',
    },
    propDefinitions: [
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Primary color',
        defaultValue: '#3b82f6',
      },
      {
        name: 'secondaryColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Secondary color for alternating dots',
        defaultValue: undefined,
      },
      {
        name: 'dotCount',
        type: 'number',
        control: { type: 'range', min: 2, max: 8, step: 1 },
        description: 'Number of dots',
        defaultValue: 3,
      },
      {
        name: 'dotSize',
        type: 'number',
        control: { type: 'range', min: 6, max: 20, step: 2 },
        description: 'Size of each dot',
        defaultValue: 10,
      },
      {
        name: 'gap',
        type: 'number',
        control: { type: 'range', min: 4, max: 20, step: 2 },
        description: 'Gap between dots',
        defaultValue: 8,
      },
      {
        name: 'bounceHeight',
        type: 'number',
        control: { type: 'range', min: 0.5, max: 3, step: 0.1 },
        description: 'Bounce height multiplier',
        defaultValue: 1,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Five Dots', props: { dotCount: 5 } },
      { name: 'High Bounce', props: { bounceHeight: 2 } },
      { name: 'Two Color', props: { color: '#3b82f6', secondaryColor: '#ec4899', dotCount: 4 } },
    ],
    importPath: "import { BouncingDots } from 'premium-react-loaders';",
  },
  {
    id: 'bouncing-balls',
    name: 'BouncingBalls',
    category: 'bounce',
    component: BouncingBalls,
    description: '3D-style bouncing balls with physics',
    defaultProps: {
      color: '#3b82f6',
      ballCount: 3,
      ballSize: 14,
      gap: 10,
      bounceHeight: 1.5,
      shadow: true,
      squash: true,
      speed: 'normal',
    },
    propDefinitions: [
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Primary color',
        defaultValue: '#3b82f6',
      },
      {
        name: 'secondaryColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Secondary color for alternating balls',
        defaultValue: undefined,
      },
      {
        name: 'ballCount',
        type: 'number',
        control: { type: 'range', min: 1, max: 6, step: 1 },
        description: 'Number of balls',
        defaultValue: 3,
      },
      {
        name: 'ballSize',
        type: 'number',
        control: { type: 'range', min: 8, max: 24, step: 2 },
        description: 'Size of each ball',
        defaultValue: 14,
      },
      {
        name: 'gap',
        type: 'number',
        control: { type: 'range', min: 4, max: 20, step: 2 },
        description: 'Gap between balls',
        defaultValue: 10,
      },
      {
        name: 'bounceHeight',
        type: 'number',
        control: { type: 'range', min: 0.5, max: 3, step: 0.1 },
        description: 'Bounce height multiplier',
        defaultValue: 1.5,
      },
      {
        name: 'shadow',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Enable shadow effect',
        defaultValue: true,
      },
      {
        name: 'squash',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Enable squash and stretch',
        defaultValue: true,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'No Shadow', props: { shadow: false } },
      { name: 'No Squash', props: { squash: false } },
      { name: 'High Bounce', props: { bounceHeight: 2.5 } },
    ],
    importPath: "import { BouncingBalls } from 'premium-react-loaders';",
  },

  // ==================== INFINITY COMPONENTS ====================
  {
    id: 'infinity-loader',
    name: 'InfinityLoader',
    category: 'infinity',
    component: InfinityLoader,
    description: 'Classic infinity symbol animation',
    defaultProps: {
      size: 'md',
      color: '#3b82f6',
      thickness: 3,
      gradient: false,
      showDot: false,
      speed: 'normal',
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'string',
        control: { type: 'select', options: ['sm', 'md', 'lg', 'xl'] },
        description: 'Size of the loader',
        defaultValue: 'md',
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Primary color',
        defaultValue: '#3b82f6',
      },
      {
        name: 'secondaryColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Secondary color for gradient',
        defaultValue: undefined,
      },
      {
        name: 'thickness',
        type: 'number',
        control: { type: 'range', min: 1, max: 8, step: 1 },
        description: 'Stroke thickness',
        defaultValue: 3,
      },
      {
        name: 'gradient',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Enable gradient coloring',
        defaultValue: false,
      },
      {
        name: 'showDot',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show traveling dot',
        defaultValue: false,
      },
      {
        name: 'dotSize',
        type: 'number',
        control: { type: 'range', min: 4, max: 12, step: 1 },
        description: 'Size of the dot',
        defaultValue: 6,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'With Dot', props: { size: 'lg', showDot: true } },
      { name: 'Gradient', props: { size: 'lg', gradient: true, secondaryColor: '#ec4899' } },
    ],
    importPath: "import { InfinityLoader } from 'premium-react-loaders';",
  },
  {
    id: 'mobius-loader',
    name: 'MobiusLoader',
    category: 'infinity',
    component: MobiusLoader,
    description: 'Mobius strip-style continuous loop',
    defaultProps: {
      size: 'md',
      color: '#3b82f6',
      segments: 6,
      twist: true,
      thickness: 2,
      ribbonWidth: 8,
      speed: 'normal',
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'string',
        control: { type: 'select', options: ['sm', 'md', 'lg', 'xl'] },
        description: 'Size of the loader',
        defaultValue: 'md',
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Primary color',
        defaultValue: '#3b82f6',
      },
      {
        name: 'secondaryColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Secondary color for alternating segments',
        defaultValue: undefined,
      },
      {
        name: 'segments',
        type: 'number',
        control: { type: 'range', min: 4, max: 12, step: 1 },
        description: 'Number of segments',
        defaultValue: 6,
      },
      {
        name: 'thickness',
        type: 'number',
        control: { type: 'range', min: 1, max: 6, step: 1 },
        description: 'Segment thickness',
        defaultValue: 2,
      },
      {
        name: 'ribbonWidth',
        type: 'number',
        control: { type: 'range', min: 4, max: 16, step: 2 },
        description: 'Width of each ribbon segment',
        defaultValue: 8,
      },
      {
        name: 'twist',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Enable twist effect',
        defaultValue: true,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Many Segments', props: { size: 'lg', segments: 10 } },
      { name: 'No Twist', props: { twist: false } },
      { name: 'Two Color', props: { color: '#3b82f6', secondaryColor: '#ec4899', segments: 8 } },
    ],
    importPath: "import { MobiusLoader } from 'premium-react-loaders';",
  },

  // ==================== TEXT COMPONENTS ====================
  {
    id: 'loading-text',
    name: 'LoadingText',
    category: 'text',
    component: LoadingText,
    description: 'Animated loading text with ellipsis',
    defaultProps: {
      text: 'Loading',
      animation: 'dots',
      fontSize: 16,
      fontWeight: 500,
      color: '#374151',
      dotCount: 3,
      showEllipsis: true,
      speed: 'normal',
    },
    propDefinitions: [
      {
        name: 'text',
        type: 'string',
        control: { type: 'text' },
        description: 'The text to display',
        defaultValue: 'Loading',
      },
      {
        name: 'animation',
        type: 'string',
        control: { type: 'select', options: ['dots', 'fade', 'bounce', 'wave'] },
        description: 'Animation style',
        defaultValue: 'dots',
      },
      {
        name: 'fontSize',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Font size',
        defaultValue: 16,
      },
      {
        name: 'fontWeight',
        type: 'number | string',
        control: { type: 'select', options: ['400', '500', '600', '700'] },
        description: 'Font weight',
        defaultValue: 500,
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Text color',
        defaultValue: '#374151',
      },
      {
        name: 'dotCount',
        type: 'number',
        control: { type: 'range', min: 1, max: 5, step: 1 },
        description: 'Number of dots',
        defaultValue: 3,
      },
      {
        name: 'showEllipsis',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show ellipsis after text',
        defaultValue: true,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Custom Text', props: { text: 'Please wait' } },
      { name: 'Fade Animation', props: { animation: 'fade' } },
      { name: 'Bounce Animation', props: { animation: 'bounce' } },
      { name: 'Wave Animation', props: { animation: 'wave' } },
      { name: 'Large Text', props: { fontSize: 24, fontWeight: 600 } },
    ],
    importPath: "import { LoadingText } from 'premium-react-loaders';",
  },

  // ==================== 3D COMPONENTS ====================
  {
    id: 'cube-spinner',
    name: 'CubeSpinner',
    category: '3d',
    component: CubeSpinner,
    description: '3D rotating cube with customizable face colors and rotation axis',
    defaultProps: {
      size: 'md',
      color: '#3b82f6',
      rotationAxis: 'diagonal',
      perspective: 600,
      showEdges: false,
      edgeColor: '#000000',
      speed: 'normal',
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Size of the cube',
        defaultValue: 'md',
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Default color for all faces',
        defaultValue: '#3b82f6',
      },
      {
        name: 'faceColors',
        type: 'string[]',
        control: { type: 'text' },
        description: 'Array of 6 colors for each face [front, back, right, left, top, bottom]',
        defaultValue: undefined,
      },
      {
        name: 'rotationAxis',
        type: 'string',
        control: { type: 'select', options: ['x', 'y', 'z', 'diagonal'] },
        description: 'Rotation axis',
        defaultValue: 'diagonal',
      },
      {
        name: 'perspective',
        type: 'number',
        control: { type: 'range', min: 200, max: 1000, step: 50 },
        description: 'CSS perspective value',
        defaultValue: 600,
      },
      {
        name: 'showEdges',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show cube edges',
        defaultValue: false,
      },
      {
        name: 'edgeColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Edge color',
        defaultValue: '#000000',
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Rotate X', props: { rotationAxis: 'x' } },
      { name: 'Rotate Y', props: { rotationAxis: 'y' } },
      { name: 'With Edges', props: { showEdges: true, edgeColor: '#ffffff' } },
    ],
    importPath: "import { CubeSpinner } from 'premium-react-loaders';",
  },
  {
    id: 'flip-card',
    name: 'FlipCard',
    category: '3d',
    component: FlipCard,
    description: '3D flipping card effect with front and back faces',
    defaultProps: {
      size: 'md',
      color: '#3b82f6',
      secondaryColor: '#8b5cf6',
      axis: 'y',
      flipOnHover: false,
      autoFlip: true,
      speed: 'normal',
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Size of the card',
        defaultValue: 'md',
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Front face color',
        defaultValue: '#3b82f6',
      },
      {
        name: 'secondaryColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Back face color',
        defaultValue: '#8b5cf6',
      },
      {
        name: 'axis',
        type: 'string',
        control: { type: 'select', options: ['x', 'y'] },
        description: 'Flip axis',
        defaultValue: 'y',
      },
      {
        name: 'flipOnHover',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Flip on mouse hover',
        defaultValue: false,
      },
      {
        name: 'autoFlip',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Auto-flip animation',
        defaultValue: true,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Flip X', props: { axis: 'x' } },
      { name: 'Hover to Flip', props: { flipOnHover: true, autoFlip: false } },
    ],
    importPath: "import { FlipCard } from 'premium-react-loaders';",
  },
  {
    id: 'plane-rotate',
    name: 'PlaneRotate',
    category: '3d',
    component: PlaneRotate,
    description: '3D rotating plane with perspective effect',
    defaultProps: {
      size: 'md',
      color: '#3b82f6',
      pattern: 'solid',
      patternColor: '#ffffff',
      rotationAxis: 'xy',
      speed: 'normal',
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Size of the plane',
        defaultValue: 'md',
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Plane color',
        defaultValue: '#3b82f6',
      },
      {
        name: 'pattern',
        type: 'string',
        control: { type: 'select', options: ['solid', 'grid', 'dots'] },
        description: 'Surface pattern',
        defaultValue: 'solid',
      },
      {
        name: 'patternColor',
        type: 'string',
        control: { type: 'color' },
        description: 'Pattern color',
        defaultValue: '#ffffff',
      },
      {
        name: 'rotationAxis',
        type: 'string',
        control: { type: 'select', options: ['x', 'y', 'xy'] },
        description: 'Rotation axis',
        defaultValue: 'xy',
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Grid Pattern', props: { pattern: 'grid' } },
      { name: 'Dots Pattern', props: { pattern: 'dots' } },
    ],
    importPath: "import { PlaneRotate } from 'premium-react-loaders';",
  },
  {
    id: 'helix',
    name: 'Helix',
    category: '3d',
    component: Helix,
    description: '3D helix/spiral animation with rotating particles',
    defaultProps: {
      size: 'md',
      color: '#3b82f6',
      particles: 12,
      helixHeight: 1.5,
      radius: 0.4,
      speed: 'normal',
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Size of the helix',
        defaultValue: 'md',
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Particle color',
        defaultValue: '#3b82f6',
      },
      {
        name: 'particles',
        type: 'number',
        control: { type: 'range', min: 6, max: 20, step: 2 },
        description: 'Number of particles',
        defaultValue: 12,
      },
      {
        name: 'helixHeight',
        type: 'number',
        control: { type: 'range', min: 1, max: 3, step: 0.1 },
        description: 'Height multiplier',
        defaultValue: 1.5,
      },
      {
        name: 'radius',
        type: 'number',
        control: { type: 'range', min: 0.2, max: 0.8, step: 0.1 },
        description: 'Helix radius',
        defaultValue: 0.4,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Many Particles', props: { particles: 18 } },
      { name: 'Tall Helix', props: { helixHeight: 2.5 } },
    ],
    importPath: "import { Helix } from 'premium-react-loaders';",
  },
  {
    id: 'perspective-ring',
    name: 'PerspectiveRing',
    category: '3d',
    component: PerspectiveRing,
    description: '3D ring with perspective tilt and rotation',
    defaultProps: {
      size: 'md',
      color: '#3b82f6',
      thickness: 8,
      tilt: 60,
      segments: 8,
      speed: 'normal',
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'size',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Size of the ring',
        defaultValue: 'md',
      },
      {
        name: 'color',
        type: 'string',
        control: { type: 'color' },
        description: 'Ring color',
        defaultValue: '#3b82f6',
      },
      {
        name: 'thickness',
        type: 'number',
        control: { type: 'range', min: 4, max: 16, step: 2 },
        description: 'Ring thickness',
        defaultValue: 8,
      },
      {
        name: 'tilt',
        type: 'number',
        control: { type: 'range', min: 0, max: 90, step: 5 },
        description: 'Tilt angle in degrees',
        defaultValue: 60,
      },
      {
        name: 'segments',
        type: 'number',
        control: { type: 'range', min: 4, max: 16, step: 2 },
        description: 'Number of segments',
        defaultValue: 8,
      },
      {
        name: 'speed',
        type: 'string',
        control: { type: 'select', options: ['slow', 'normal', 'fast'] },
        description: 'Animation speed',
        defaultValue: 'normal',
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Flat Ring', props: { tilt: 0 } },
      { name: 'Thick Ring', props: { thickness: 12 } },
    ],
    importPath: "import { PerspectiveRing } from 'premium-react-loaders';",
  },

  // ==================== SMART COMPONENTS ====================
  {
    id: 'smart-skeleton',
    name: 'SmartSkeleton',
    category: 'skeleton',
    component: SmartSkeleton,
    description: 'Intelligent skeleton that auto-detects content type and generates appropriate loading placeholder',
    defaultProps: {
      contentType: 'text',
      itemCount: 3,
      detectionStrategy: 'mixed',
      animate: true,
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'contentType',
        type: 'string',
        control: {
          type: 'select',
          options: ['auto', 'text', 'image', 'card', 'list', 'table', 'form'],
        },
        description: 'Content type to render',
        defaultValue: 'text',
      },
      {
        name: 'itemCount',
        type: 'number',
        control: { type: 'range', min: 1, max: 10, step: 1 },
        description: 'Number of items for list/table types',
        defaultValue: 3,
      },
      {
        name: 'detectionStrategy',
        type: 'string',
        control: { type: 'select', options: ['dom', 'dimensions', 'mixed'] },
        description: 'Strategy for auto-detection',
        defaultValue: 'mixed',
      },
      {
        name: 'width',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Width override',
        defaultValue: undefined,
      },
      {
        name: 'height',
        type: 'number | string',
        control: { type: 'text' },
        description: 'Height override',
        defaultValue: undefined,
      },
      {
        name: 'animate',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Enable shimmer animation',
        defaultValue: true,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Card', props: { contentType: 'card' } },
      { name: 'Table', props: { contentType: 'table', itemCount: 5 } },
      { name: 'List', props: { contentType: 'list', itemCount: 4 } },
    ],
    importPath: "import { SmartSkeleton } from 'premium-react-loaders';",
  },
  {
    id: 'data-table-skeleton',
    name: 'DataTableSkeleton',
    category: 'skeleton',
    component: DataTableSkeleton,
    description: 'Advanced table skeleton with headers, sorting, filters, pagination, and actions',
    defaultProps: {
      rows: 5,
      columns: 4,
      showHeader: true,
      showSortIndicators: false,
      showFilters: false,
      showPagination: false,
      showSelection: false,
      showActions: false,
      rowHeight: 48,
      striped: false,
      width: '100%',
      animate: true,
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'rows',
        type: 'number',
        control: { type: 'range', min: 1, max: 20, step: 1 },
        description: 'Number of data rows',
        defaultValue: 5,
      },
      {
        name: 'columns',
        type: 'number',
        control: { type: 'range', min: 2, max: 10, step: 1 },
        description: 'Number of columns',
        defaultValue: 4,
      },
      {
        name: 'showHeader',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show table header',
        defaultValue: true,
      },
      {
        name: 'showSortIndicators',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show sort indicators in headers',
        defaultValue: false,
      },
      {
        name: 'showFilters',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show filter row',
        defaultValue: false,
      },
      {
        name: 'showPagination',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show pagination controls',
        defaultValue: false,
      },
      {
        name: 'showSelection',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show selection checkboxes',
        defaultValue: false,
      },
      {
        name: 'showActions',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show actions column',
        defaultValue: false,
      },
      {
        name: 'rowHeight',
        type: 'number',
        control: { type: 'range', min: 32, max: 80, step: 4 },
        description: 'Height of each row',
        defaultValue: 48,
      },
      {
        name: 'striped',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Alternating row colors',
        defaultValue: false,
      },
      {
        name: 'animate',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Enable shimmer animation',
        defaultValue: true,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Full Featured', props: { rows: 8, showSortIndicators: true, showFilters: true, showPagination: true } },
      { name: 'With Selection', props: { showSelection: true, showActions: true } },
      { name: 'Striped', props: { striped: true, rows: 6 } },
    ],
    importPath: "import { DataTableSkeleton } from 'premium-react-loaders';",
  },
  {
    id: 'form-field-loader',
    name: 'FormFieldLoader',
    category: 'smart',
    component: FormFieldLoader,
    description: 'Loading state for form inputs that maintains layout during loading',
    defaultProps: {
      size: 'md',
      fieldType: 'text',
      showLabel: true,
      labelWidth: 100,
      fieldWidth: '100%',
      showValidation: false,
      showRequired: false,
      showHelperText: false,
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'fieldType',
        type: 'string',
        control: { type: 'select', options: ['text', 'select', 'checkbox', 'radio', 'textarea', 'file'] },
        description: 'Type of form field',
        defaultValue: 'text',
      },
      {
        name: 'showLabel',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show label skeleton',
        defaultValue: true,
      },
      {
        name: 'labelWidth',
        type: 'number',
        control: { type: 'range', min: 50, max: 200, step: 10 },
        description: 'Width of label skeleton',
        defaultValue: 100,
      },
      {
        name: 'fieldWidth',
        type: 'string',
        control: { type: 'text' },
        description: 'Width of field',
        defaultValue: '100%',
      },
      {
        name: 'fieldHeight',
        type: 'number',
        control: { type: 'range', min: 40, max: 200, step: 10 },
        description: 'Height of field (for textarea)',
        defaultValue: undefined,
      },
      {
        name: 'showValidation',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show validation message skeleton',
        defaultValue: false,
      },
      {
        name: 'showRequired',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show required indicator',
        defaultValue: false,
      },
      {
        name: 'showHelperText',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Show helper text skeleton',
        defaultValue: false,
      },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Select', props: { fieldType: 'select' } },
      { name: 'Textarea', props: { fieldType: 'textarea', fieldHeight: 120 } },
      { name: 'Checkbox', props: { fieldType: 'checkbox' } },
      { name: 'With Validation', props: { showValidation: true, showRequired: true } },
    ],
    importPath: "import { FormFieldLoader } from 'premium-react-loaders';",
  },

  // ==================== ACCESSIBILITY COMPONENTS ====================
  {
    id: 'live-region',
    name: 'LiveRegion',
    category: 'accessibility',
    component: LiveRegion,
    description: 'ARIA live region for screen reader announcements of loading states',
    defaultProps: {
      message: '',
      politeness: 'polite',
      atomic: true,
      delay: 0,
      minDuration: 0,
      transition: false,
    },
    propDefinitions: [
      {
        name: 'message',
        type: 'string',
        control: { type: 'text' },
        description: 'Message to announce',
        defaultValue: '',
      },
      {
        name: 'politeness',
        type: 'string',
        control: { type: 'select', options: ['polite', 'assertive', 'off'] },
        description: 'ARIA live politeness level',
        defaultValue: 'polite',
      },
      {
        name: 'clearAfter',
        type: 'number',
        control: { type: 'range', min: 1000, max: 10000, step: 500 },
        description: 'Auto-clear message after milliseconds',
        defaultValue: undefined,
      },
      {
        name: 'atomic',
        type: 'boolean',
        control: { type: 'boolean' },
        description: 'Announce entire region (vs changes only)',
        defaultValue: true,
      },
    ],
    examples: [
      { name: 'Default', props: { message: 'Loading data...' } },
      { name: 'Polite', props: { message: 'Loading complete', politeness: 'polite' } },
      { name: 'Assertive', props: { message: 'Error occurred', politeness: 'assertive' } },
      { name: 'Auto Clear', props: { message: 'Progress: 50%', clearAfter: 3000 } },
    ],
    importPath: "import { LiveRegion } from 'premium-react-loaders';",
  },
  // ==================== MORPH COMPONENTS ====================
  {
    id: 'morph-blob',
    name: 'MorphBlob',
    category: 'morph',
    component: MorphBlob,
    description: 'Organic blob shape that continuously morphs with smooth CSS animation',
    defaultProps: { size: 80, color: '#3b82f6', speed: 'normal' },
    propDefinitions: [
      { name: 'size', type: 'number', control: { type: 'range', min: 40, max: 200, step: 8 }, description: 'Diameter in px', defaultValue: 80 },
      { name: 'color', type: 'string', control: { type: 'color' }, description: 'Fill color', defaultValue: '#3b82f6' },
      { name: 'speed', type: 'string', control: { type: 'select', options: ['slow', 'normal', 'fast'] }, description: 'Morphing speed', defaultValue: 'normal' },
    ],
    examples: [
      { name: 'Default', props: { size: 80, color: '#3b82f6' } },
      { name: 'Large', props: { size: 120, color: '#8b5cf6', speed: 'slow' } },
    ],
    importPath: "import { MorphBlob } from 'premium-react-loaders';",
  },
  {
    id: 'liquid-fill',
    name: 'LiquidFill',
    category: 'morph',
    component: LiquidFill,
    description: 'Container that fills with animated liquid; supports determinate and indeterminate modes',
    defaultProps: { value: 65, size: 80, color: '#3b82f6' },
    propDefinitions: [
      { name: 'value', type: 'number', control: { type: 'range', min: 0, max: 100, step: 1 }, description: 'Fill percentage (0–100); omit for indeterminate', defaultValue: 65 },
      { name: 'size', type: 'number', control: { type: 'range', min: 40, max: 200, step: 8 }, description: 'Diameter in px', defaultValue: 80 },
      { name: 'color', type: 'string', control: { type: 'color' }, description: 'Liquid color', defaultValue: '#3b82f6' },
      { name: 'waveAmplitude', type: 'number', control: { type: 'range', min: 1, max: 12, step: 1 }, description: 'Wave height in px', defaultValue: 4 },
    ],
    examples: [
      { name: 'Half Full', props: { value: 50, color: '#3b82f6' } },
      { name: 'Almost Full', props: { value: 90, color: '#10b981' } },
      { name: 'Indeterminate', props: {} },
    ],
    importPath: "import { LiquidFill } from 'premium-react-loaders';",
  },
  {
    id: 'wave-circle',
    name: 'WaveCircle',
    category: 'morph',
    component: WaveCircle,
    description: 'Concentric ripple rings expanding outward like a sonar pulse',
    defaultProps: { size: 80, color: '#3b82f6', ringCount: 3 },
    propDefinitions: [
      { name: 'size', type: 'number', control: { type: 'range', min: 40, max: 200, step: 8 }, description: 'Max diameter in px', defaultValue: 80 },
      { name: 'color', type: 'string', control: { type: 'color' }, description: 'Ring color', defaultValue: '#3b82f6' },
      { name: 'ringCount', type: 'number', control: { type: 'range', min: 2, max: 5, step: 1 }, description: 'Number of rings (2–5)', defaultValue: 3 },
      { name: 'speed', type: 'string', control: { type: 'select', options: ['slow', 'normal', 'fast'] }, description: 'Expansion speed', defaultValue: 'normal' },
    ],
    examples: [
      { name: 'Default', props: { ringCount: 3 } },
      { name: 'Dense', props: { ringCount: 5, color: '#8b5cf6' } },
    ],
    importPath: "import { WaveCircle } from 'premium-react-loaders';",
  },
  // ==================== GRADIENT COMPONENTS ====================
  {
    id: 'gradient-spinner',
    name: 'GradientSpinner',
    category: 'gradient',
    component: GradientSpinner,
    description: 'Circular spinner with a sweeping conic gradient animation',
    defaultProps: { size: 48, color: '#3b82f6', secondaryColor: '#8b5cf6', thickness: 4 },
    propDefinitions: [
      { name: 'size', type: 'number', control: { type: 'range', min: 24, max: 120, step: 8 }, description: 'Diameter in px', defaultValue: 48 },
      { name: 'color', type: 'string', control: { type: 'color' }, description: 'Gradient start color', defaultValue: '#3b82f6' },
      { name: 'secondaryColor', type: 'string', control: { type: 'color' }, description: 'Gradient end color', defaultValue: '#8b5cf6' },
      { name: 'thickness', type: 'number', control: { type: 'range', min: 2, max: 16, step: 1 }, description: 'Ring stroke width in px', defaultValue: 4 },
      { name: 'speed', type: 'string', control: { type: 'select', options: ['slow', 'normal', 'fast'] }, description: 'Spin speed', defaultValue: 'normal' },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Warm', props: { color: '#f59e0b', secondaryColor: '#ef4444', thickness: 6 } },
    ],
    importPath: "import { GradientSpinner } from 'premium-react-loaders';",
  },
  {
    id: 'gradient-ring',
    name: 'GradientRing',
    category: 'gradient',
    component: GradientRing,
    description: 'Ring with a flowing multi-stop gradient that rotates around the circumference',
    defaultProps: { size: 48, colors: ['#3b82f6', '#8b5cf6', '#ec4899'], thickness: 6 },
    propDefinitions: [
      { name: 'size', type: 'number', control: { type: 'range', min: 24, max: 120, step: 8 }, description: 'Diameter in px', defaultValue: 48 },
      { name: 'thickness', type: 'number', control: { type: 'range', min: 2, max: 20, step: 1 }, description: 'Ring width in px', defaultValue: 6 },
      { name: 'speed', type: 'string', control: { type: 'select', options: ['slow', 'normal', 'fast'] }, description: 'Animation speed', defaultValue: 'normal' },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Rainbow', props: { colors: ['#ef4444', '#f59e0b', '#10b981', '#3b82f6'], thickness: 8 } },
    ],
    importPath: "import { GradientRing } from 'premium-react-loaders';",
  },
  {
    id: 'gradient-bar',
    name: 'GradientBar',
    category: 'gradient',
    component: GradientBar,
    description: 'Progress bar with flowing animated gradient; supports determinate and indeterminate modes',
    defaultProps: { value: 65, height: 8, color: '#3b82f6', secondaryColor: '#8b5cf6' },
    propDefinitions: [
      { name: 'value', type: 'number', control: { type: 'range', min: 0, max: 100, step: 1 }, description: 'Progress (0–100); omit for indeterminate', defaultValue: 65 },
      { name: 'height', type: 'number', control: { type: 'range', min: 4, max: 24, step: 1 }, description: 'Bar height in px', defaultValue: 8 },
      { name: 'color', type: 'string', control: { type: 'color' }, description: 'Gradient start color', defaultValue: '#3b82f6' },
      { name: 'secondaryColor', type: 'string', control: { type: 'color' }, description: 'Gradient end color', defaultValue: '#8b5cf6' },
      { name: 'speed', type: 'string', control: { type: 'select', options: ['slow', 'normal', 'fast'] }, description: 'Flow speed', defaultValue: 'normal' },
    ],
    examples: [
      { name: 'Determinate', props: { value: 65 } },
      { name: 'Indeterminate', props: {} },
      { name: 'Warm', props: { value: 80, color: '#f59e0b', secondaryColor: '#ef4444', height: 12 } },
    ],
    importPath: "import { GradientBar } from 'premium-react-loaders';",
  },
  // ==================== PARTICLE COMPONENTS ====================
  {
    id: 'particle-burst',
    name: 'ParticleBurst',
    category: 'particle',
    component: ParticleBurst,
    description: 'Particles that burst outward from a central point in a looping animation',
    defaultProps: { size: 60, count: 8, color: '#3b82f6', particleSize: 6 },
    propDefinitions: [
      { name: 'size', type: 'number', control: { type: 'range', min: 40, max: 160, step: 8 }, description: 'Burst radius in px', defaultValue: 60 },
      { name: 'count', type: 'number', control: { type: 'range', min: 4, max: 20, step: 1 }, description: 'Number of particles', defaultValue: 8 },
      { name: 'color', type: 'string', control: { type: 'color' }, description: 'Particle color', defaultValue: '#3b82f6' },
      { name: 'particleSize', type: 'number', control: { type: 'range', min: 2, max: 16, step: 1 }, description: 'Dot diameter in px', defaultValue: 6 },
      { name: 'speed', type: 'string', control: { type: 'select', options: ['slow', 'normal', 'fast'] }, description: 'Burst cycle speed', defaultValue: 'normal' },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Dense', props: { count: 16, particleSize: 4, color: '#8b5cf6' } },
    ],
    importPath: "import { ParticleBurst } from 'premium-react-loaders';",
  },
  {
    id: 'particle-orbit',
    name: 'ParticleOrbit',
    category: 'particle',
    component: ParticleOrbit,
    description: 'Particles orbiting a central dot with staggered animation offsets',
    defaultProps: { size: 80, count: 5, color: '#3b82f6' },
    propDefinitions: [
      { name: 'size', type: 'number', control: { type: 'range', min: 40, max: 160, step: 8 }, description: 'Orbital diameter in px', defaultValue: 80 },
      { name: 'count', type: 'number', control: { type: 'range', min: 3, max: 8, step: 1 }, description: 'Number of particles', defaultValue: 5 },
      { name: 'color', type: 'string', control: { type: 'color' }, description: 'Particle color', defaultValue: '#3b82f6' },
      { name: 'centerColor', type: 'string', control: { type: 'color' }, description: 'Central dot color (defaults to color)', defaultValue: '#3b82f6' },
      { name: 'speed', type: 'string', control: { type: 'select', options: ['slow', 'normal', 'fast'] }, description: 'Orbit speed', defaultValue: 'normal' },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Multicolor', props: { count: 6, color: '#8b5cf6', centerColor: '#ef4444' } },
    ],
    importPath: "import { ParticleOrbit } from 'premium-react-loaders';",
  },
  // ==================== NEON COMPONENTS ====================
  {
    id: 'neon-pulse',
    name: 'NeonPulse',
    category: 'neon',
    component: NeonPulse,
    description: 'Pulsing glowing ring with neon box-shadow animation',
    defaultProps: { size: 60, color: '#3b82f6', glowIntensity: 'medium' },
    propDefinitions: [
      { name: 'size', type: 'number', control: { type: 'range', min: 40, max: 160, step: 8 }, description: 'Ring diameter in px', defaultValue: 60 },
      { name: 'color', type: 'string', control: { type: 'color' }, description: 'Neon glow color', defaultValue: '#3b82f6' },
      { name: 'glowIntensity', type: 'string', control: { type: 'select', options: ['low', 'medium', 'high'] }, description: 'Glow spread intensity', defaultValue: 'medium' },
      { name: 'speed', type: 'string', control: { type: 'select', options: ['slow', 'normal', 'fast'] }, description: 'Pulse speed', defaultValue: 'normal' },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'High glow', props: { glowIntensity: 'high', color: '#22d3ee', size: 80 } },
    ],
    importPath: "import { NeonPulse } from 'premium-react-loaders';",
  },
  {
    id: 'neon-spinner',
    name: 'NeonSpinner',
    category: 'neon',
    component: NeonSpinner,
    description: 'Spinning arc with neon glow trail effect',
    defaultProps: { size: 60, color: '#a855f7', thickness: 4 },
    propDefinitions: [
      { name: 'size', type: 'number', control: { type: 'range', min: 40, max: 160, step: 8 }, description: 'Spinner diameter in px', defaultValue: 60 },
      { name: 'color', type: 'string', control: { type: 'color' }, description: 'Neon arc color', defaultValue: '#a855f7' },
      { name: 'thickness', type: 'number', control: { type: 'range', min: 2, max: 12, step: 1 }, description: 'Arc stroke width in px', defaultValue: 4 },
      { name: 'speed', type: 'string', control: { type: 'select', options: ['slow', 'normal', 'fast'] }, description: 'Spin speed', defaultValue: 'normal' },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Thick cyan', props: { color: '#22d3ee', thickness: 6, size: 80 } },
    ],
    importPath: "import { NeonSpinner } from 'premium-react-loaders';",
  },
  {
    id: 'neon-text',
    name: 'NeonText',
    category: 'neon',
    component: NeonText,
    description: 'Loading text with pulsing neon glow animation',
    defaultProps: { text: 'LOADING', color: '#22d3ee', fontSize: 18 },
    propDefinitions: [
      { name: 'text', type: 'string', control: { type: 'text' }, description: 'Display text', defaultValue: 'LOADING' },
      { name: 'color', type: 'string', control: { type: 'color' }, description: 'Neon glow color', defaultValue: '#22d3ee' },
      { name: 'fontSize', type: 'number', control: { type: 'range', min: 10, max: 48, step: 2 }, description: 'Font size in px', defaultValue: 18 },
      { name: 'speed', type: 'string', control: { type: 'select', options: ['slow', 'normal', 'fast'] }, description: 'Glow pulse speed', defaultValue: 'normal' },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Large purple', props: { text: 'PROCESSING', color: '#a855f7', fontSize: 24 } },
    ],
    importPath: "import { NeonText } from 'premium-react-loaders';",
  },
  // ==================== STATUS EXTENSIONS ====================
  {
    id: 'warning-indicator',
    name: 'WarningIndicator',
    category: 'status',
    component: WarningIndicator,
    description: 'Animated warning triangle with exclamation mark',
    defaultProps: { size: 48, color: '#f59e0b', animate: true, shake: false },
    propDefinitions: [
      { name: 'size', type: 'number', control: { type: 'range', min: 24, max: 120, step: 8 }, description: 'Icon size in px', defaultValue: 48 },
      { name: 'color', type: 'string', control: { type: 'color' }, description: 'Triangle color', defaultValue: '#f59e0b' },
      { name: 'animate', type: 'boolean', control: { type: 'boolean' }, description: 'Run draw-in animation', defaultValue: true },
      { name: 'shake', type: 'boolean', control: { type: 'boolean' }, description: 'Shake on appearance', defaultValue: false },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Shake', props: { shake: true } },
    ],
    importPath: "import { WarningIndicator } from 'premium-react-loaders';",
  },
  {
    id: 'info-indicator',
    name: 'InfoIndicator',
    category: 'status',
    component: InfoIndicator,
    description: 'Animated info badge with optional pulse glow',
    defaultProps: { size: 48, color: '#3b82f6', animate: true, pulse: false },
    propDefinitions: [
      { name: 'size', type: 'number', control: { type: 'range', min: 24, max: 120, step: 8 }, description: 'Icon size in px', defaultValue: 48 },
      { name: 'color', type: 'string', control: { type: 'color' }, description: 'Icon color', defaultValue: '#3b82f6' },
      { name: 'animate', type: 'boolean', control: { type: 'boolean' }, description: 'Run draw-in animation', defaultValue: true },
      { name: 'pulse', type: 'boolean', control: { type: 'boolean' }, description: 'Ongoing pulse glow', defaultValue: false },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'With pulse', props: { pulse: true } },
    ],
    importPath: "import { InfoIndicator } from 'premium-react-loaders';",
  },
  // ==================== PARTICLE EXTENSIONS ====================
  {
    id: 'particle-trail',
    name: 'ParticleTrail',
    category: 'particle',
    component: ParticleTrail,
    description: 'Particles trailing along a circular orbit path with opacity fade',
    defaultProps: { size: 60, count: 6, color: '#3b82f6' },
    propDefinitions: [
      { name: 'size', type: 'number', control: { type: 'range', min: 40, max: 160, step: 8 }, description: 'Orbit diameter in px', defaultValue: 60 },
      { name: 'count', type: 'number', control: { type: 'range', min: 3, max: 10, step: 1 }, description: 'Trailing particle count', defaultValue: 6 },
      { name: 'color', type: 'string', control: { type: 'color' }, description: 'Particle color', defaultValue: '#3b82f6' },
      { name: 'speed', type: 'string', control: { type: 'select', options: ['slow', 'normal', 'fast'] }, description: 'Orbit speed', defaultValue: 'normal' },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Dense purple', props: { count: 10, color: '#8b5cf6', size: 80 } },
    ],
    importPath: "import { ParticleTrail } from 'premium-react-loaders';",
  },
  {
    id: 'particle-field',
    name: 'ParticleField',
    category: 'particle',
    component: ParticleField,
    description: 'Ambient floating particle field with rising drift animation',
    defaultProps: { width: 120, height: 120, count: 12, color: '#3b82f6' },
    propDefinitions: [
      { name: 'width', type: 'number', control: { type: 'range', min: 60, max: 300, step: 20 }, description: 'Field width in px', defaultValue: 120 },
      { name: 'height', type: 'number', control: { type: 'range', min: 60, max: 300, step: 20 }, description: 'Field height in px', defaultValue: 120 },
      { name: 'count', type: 'number', control: { type: 'range', min: 5, max: 30, step: 1 }, description: 'Number of particles', defaultValue: 12 },
      { name: 'color', type: 'string', control: { type: 'color' }, description: 'Particle color', defaultValue: '#3b82f6' },
      { name: 'speed', type: 'string', control: { type: 'select', options: ['slow', 'normal', 'fast'] }, description: 'Float speed', defaultValue: 'normal' },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Dense', props: { count: 24, color: '#8b5cf6', width: 160, height: 160 } },
    ],
    importPath: "import { ParticleField } from 'premium-react-loaders';",
  },
  // ==================== GRADIENT EXTENSIONS ====================
  {
    id: 'gradient-text',
    name: 'GradientText',
    category: 'gradient',
    component: GradientText,
    description: 'Loading text filled with a flowing animated gradient',
    defaultProps: { text: 'LOADING', color: '#3b82f6', secondaryColor: '#ec4899', fontSize: 18 },
    propDefinitions: [
      { name: 'text', type: 'string', control: { type: 'text' }, description: 'Display text', defaultValue: 'LOADING' },
      { name: 'color', type: 'string', control: { type: 'color' }, description: 'Gradient start color', defaultValue: '#3b82f6' },
      { name: 'secondaryColor', type: 'string', control: { type: 'color' }, description: 'Gradient end color', defaultValue: '#ec4899' },
      { name: 'fontSize', type: 'number', control: { type: 'range', min: 10, max: 48, step: 2 }, description: 'Font size in px', defaultValue: 18 },
      { name: 'speed', type: 'string', control: { type: 'select', options: ['slow', 'normal', 'fast'] }, description: 'Flow speed', defaultValue: 'normal' },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Warm large', props: { text: 'PROCESSING', color: '#f59e0b', secondaryColor: '#ef4444', fontSize: 28 } },
    ],
    importPath: "import { GradientText } from 'premium-react-loaders';",
  },
  {
    id: 'gradient-dots',
    name: 'GradientDots',
    category: 'gradient',
    component: GradientDots,
    description: 'Bouncing dots, each filled with a shared animated gradient',
    defaultProps: { dotCount: 3, dotSize: 12, color: '#3b82f6', secondaryColor: '#ec4899' },
    propDefinitions: [
      { name: 'dotCount', type: 'number', control: { type: 'range', min: 2, max: 8, step: 1 }, description: 'Number of dots', defaultValue: 3 },
      { name: 'dotSize', type: 'number', control: { type: 'range', min: 4, max: 24, step: 1 }, description: 'Dot diameter in px', defaultValue: 12 },
      { name: 'color', type: 'string', control: { type: 'color' }, description: 'Gradient start color', defaultValue: '#3b82f6' },
      { name: 'secondaryColor', type: 'string', control: { type: 'color' }, description: 'Gradient end color', defaultValue: '#ec4899' },
      { name: 'speed', type: 'string', control: { type: 'select', options: ['slow', 'normal', 'fast'] }, description: 'Bounce speed', defaultValue: 'normal' },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Five dots', props: { dotCount: 5, dotSize: 14, color: '#f59e0b', secondaryColor: '#ef4444' } },
    ],
    importPath: "import { GradientDots } from 'premium-react-loaders';",
  },
  // ==================== NEON EXTENSIONS ====================
  {
    id: 'neon-progress',
    name: 'NeonProgress',
    category: 'neon',
    component: NeonProgress,
    description: 'Progress bar with a glowing neon fill; supports determinate and indeterminate modes',
    defaultProps: { value: 65, height: 8, color: '#3b82f6' },
    propDefinitions: [
      { name: 'value', type: 'number', control: { type: 'range', min: 0, max: 100, step: 1 }, description: 'Progress (0–100); omit for indeterminate', defaultValue: 65 },
      { name: 'height', type: 'number', control: { type: 'range', min: 4, max: 24, step: 1 }, description: 'Bar height in px', defaultValue: 8 },
      { name: 'color', type: 'string', control: { type: 'color' }, description: 'Neon glow color', defaultValue: '#3b82f6' },
      { name: 'speed', type: 'string', control: { type: 'select', options: ['slow', 'normal', 'fast'] }, description: 'Animation speed', defaultValue: 'normal' },
    ],
    examples: [
      { name: 'Determinate', props: { value: 65 } },
      { name: 'Indeterminate', props: {} },
      { name: 'Cyan', props: { value: 80, color: '#22d3ee', height: 12 } },
    ],
    importPath: "import { NeonProgress } from 'premium-react-loaders';",
  },
  {
    id: 'neon-dots',
    name: 'NeonDots',
    category: 'neon',
    component: NeonDots,
    description: 'Bouncing dots with a glowing neon box-shadow',
    defaultProps: { dotCount: 3, dotSize: 12, color: '#22d3ee' },
    propDefinitions: [
      { name: 'dotCount', type: 'number', control: { type: 'range', min: 2, max: 8, step: 1 }, description: 'Number of dots', defaultValue: 3 },
      { name: 'dotSize', type: 'number', control: { type: 'range', min: 4, max: 24, step: 1 }, description: 'Dot diameter in px', defaultValue: 12 },
      { name: 'color', type: 'string', control: { type: 'color' }, description: 'Neon glow color', defaultValue: '#22d3ee' },
      { name: 'speed', type: 'string', control: { type: 'select', options: ['slow', 'normal', 'fast'] }, description: 'Bounce speed', defaultValue: 'normal' },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Purple, four dots', props: { dotCount: 4, color: '#a855f7' } },
    ],
    importPath: "import { NeonDots } from 'premium-react-loaders';",
  },
  // ==================== CHART COMPONENTS ====================
  {
    id: 'bar-chart-skeleton',
    name: 'BarChartSkeleton',
    category: 'chart',
    component: BarChartSkeleton,
    description: 'Placeholder for a bar chart while data loads',
    defaultProps: { bars: 6, height: 120, gap: 8, showAxis: true },
    propDefinitions: [
      { name: 'bars', type: 'number', control: { type: 'range', min: 3, max: 12, step: 1 }, description: 'Number of bars', defaultValue: 6 },
      { name: 'height', type: 'number', control: { type: 'range', min: 60, max: 240, step: 10 }, description: 'Chart height in px', defaultValue: 120 },
      { name: 'gap', type: 'number', control: { type: 'range', min: 0, max: 24, step: 2 }, description: 'Gap between bars in px', defaultValue: 8 },
      { name: 'showAxis', type: 'boolean', control: { type: 'boolean' }, description: 'Show baseline axis', defaultValue: true },
      { name: 'animate', type: 'boolean', control: { type: 'boolean' }, description: 'Enable shimmer animation', defaultValue: true },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Few bars, no axis', props: { bars: 4, showAxis: false } },
    ],
    importPath: "import { BarChartSkeleton } from 'premium-react-loaders';",
  },
  {
    id: 'line-chart-skeleton',
    name: 'LineChartSkeleton',
    category: 'chart',
    component: LineChartSkeleton,
    description: 'Placeholder for a line/trend chart while data loads',
    defaultProps: { width: 240, height: 120, points: 6, showGrid: true },
    propDefinitions: [
      { name: 'width', type: 'number', control: { type: 'range', min: 120, max: 400, step: 20 }, description: 'Chart width in px', defaultValue: 240 },
      { name: 'height', type: 'number', control: { type: 'range', min: 60, max: 240, step: 10 }, description: 'Chart height in px', defaultValue: 120 },
      { name: 'points', type: 'number', control: { type: 'range', min: 3, max: 10, step: 1 }, description: 'Number of data points', defaultValue: 6 },
      { name: 'showGrid', type: 'boolean', control: { type: 'boolean' }, description: 'Show background grid lines', defaultValue: true },
      { name: 'animate', type: 'boolean', control: { type: 'boolean' }, description: 'Enable flowing dash animation', defaultValue: true },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'No grid, dense', props: { showGrid: false, points: 9 } },
    ],
    importPath: "import { LineChartSkeleton } from 'premium-react-loaders';",
  },
  {
    id: 'donut-chart-skeleton',
    name: 'DonutChartSkeleton',
    category: 'chart',
    component: DonutChartSkeleton,
    description: 'Placeholder for a donut/pie chart while data loads',
    defaultProps: { size: 120, thickness: 20, segments: 4, showLabel: true },
    propDefinitions: [
      { name: 'size', type: 'number', control: { type: 'range', min: 60, max: 240, step: 8 }, description: 'Diameter in px', defaultValue: 120 },
      { name: 'thickness', type: 'number', control: { type: 'range', min: 8, max: 40, step: 2 }, description: 'Ring thickness in px', defaultValue: 20 },
      { name: 'segments', type: 'number', control: { type: 'range', min: 2, max: 6, step: 1 }, description: 'Number of pie slices', defaultValue: 4 },
      { name: 'showLabel', type: 'boolean', control: { type: 'boolean' }, description: 'Show placeholder label in the hole', defaultValue: true },
      { name: 'animate', type: 'boolean', control: { type: 'boolean' }, description: 'Enable shimmer animation', defaultValue: true },
    ],
    examples: [
      { name: 'Default', props: {} },
      { name: 'Three slices, no label', props: { segments: 3, showLabel: false } },
    ],
    importPath: "import { DonutChartSkeleton } from 'premium-react-loaders';",
  },
];

// Helper functions
export function getComponentById(id: string): ComponentMetadata | undefined {
  return COMPONENTS.find((c) => c.id === id);
}

export function getComponentsByCategory(category: string): ComponentMetadata[] {
  return COMPONENTS.filter((c) => c.category === category);
}

export const CATEGORIES = [
  { id: 'skeleton', name: 'Skeleton', description: 'Placeholder components' },
  { id: 'spinner', name: 'Spinner', description: 'Rotating and animated spinners' },
  { id: 'progress', name: 'Progress', description: 'Progress indicators' },
  { id: 'pulse', name: 'Pulse', description: 'Pulsing and bouncing animations' },
  { id: 'overlay', name: 'Overlay', description: 'Loader overlay wrappers' },
  { id: 'button', name: 'Button', description: 'Button loading states' },
  { id: 'status', name: 'Status', description: 'Success and error indicators' },
  { id: 'transition', name: 'Transition', description: 'Smooth content transitions' },
  { id: 'shimmer', name: 'Shimmer', description: 'Shimmer effect placeholders' },
  { id: 'orbit', name: 'Orbit', description: 'Orbital and atomic animations' },
  { id: 'bounce', name: 'Bounce', description: 'Physics-based bouncing loaders' },
  { id: 'infinity', name: 'Infinity', description: 'Continuous loop animations' },
  { id: 'text', name: 'Text', description: 'Animated loading text' },
  { id: '3d', name: '3D', description: 'Immersive 3D animations' },
  { id: 'smart', name: 'Smart', description: 'Intelligent context-aware loaders' },
  { id: 'accessibility', name: 'Accessibility', description: 'Screen reader support' },
  { id: 'morph', name: 'Morph', description: 'Fluid and organic morphing animations' },
  { id: 'gradient', name: 'Gradient', description: 'Animated gradient spinners and bars' },
  { id: 'particle', name: 'Particle', description: 'Particle-based loading animations' },
  { id: 'neon', name: 'Neon', description: 'Glowing neon-style loaders' },
  { id: 'chart', name: 'Chart', description: 'Chart and dashboard skeleton placeholders' },
];
